import React, { useState, useEffect, useRef } from "react"
import { PlusIcon, PencilIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Modal from "aldoo-ra/Modal"
import Input from "aldoo-ra/Input"
import Checkbox from "aldoo-ra/Checkbox"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import { showNotification } from "aldoo-ra/Notification"
import _ from "lodash"
import { formatMoney, useStoreCurrency } from "aldoo-ra/Store/currency"

const PHASE_SLOTS = [
  { type: 'trial', icon: '🔍', label: 'Trial', color: 'bg-blue-50 border-blue-200' },
  { type: 'introductory', icon: '⭐', label: 'Introductory', color: 'bg-yellow-50 border-yellow-200' },
  { type: 'regular', icon: '💰', label: 'Regular', color: 'bg-green-50 border-green-200' }
]

function PhaseSlotEditor({ onChange, value, field, parentObject, parentValues }) {
  const [localValue, setLocalValue] = useState(value ?? [])
  // a new phase is priced in the store's currency (STORE_CURRENCY)
  const { currency: storeCurrency } = useStoreCurrency()
  const [showConfigPanel, setShowConfigPanel] = useState(false)
  const [editingSlot, setEditingSlot] = useState(null)
  const [phaseConfig, setPhaseConfig] = useState({})
  const isInternalChange = useRef(false)

  // Update value and notify parent
  const updateValue = (newValue) => {
    isInternalChange.current = true
    setLocalValue(newValue)
    onChange?.(newValue)
  }

  // Handle external value changes
  useEffect(() => {
    if (!isInternalChange.current && !_.isEqual(value, localValue)) {
      setLocalValue(value ?? [])
    }
    isInternalChange.current = false
  }, [value])

  // Get phase data for a specific slot
  const getPhaseForSlot = (slotType) => {
    return localValue.find(p => p.phase_type === slotType)
  }

  // Check if slot is configured
  const isSlotConfigured = (slotType) => {
    return !!getPhaseForSlot(slotType)
  }

  // Open configuration panel
  const handleSlotClick = (slot) => {
    const existingPhase = getPhaseForSlot(slot.type)

    if (existingPhase) {
      // Edit existing phase
      setPhaseConfig(existingPhase)
    } else {
      // Create new phase with defaults
      setPhaseConfig({
        phase_type: slot.type,
        enabled: true,
        amount: 0,
        currency: storeCurrency,
        interval: 'month',
        interval_count: 1,
        iterations: slot.type === 'regular' ? null : 1,
        requires_payment_method: true
      })
    }

    setEditingSlot(slot)
    setShowConfigPanel(true)
  }

  // Save phase configuration
  const handleSavePhase = () => {
    // Validate
    if (!phaseConfig.amount && phaseConfig.amount !== 0) {
      showNotification({
        message: 'Price amount is required',
        className: 'bg-admin_error text-white'
      })
      return
    }

    // Update or add phase
    const updatedPhases = [...localValue]
    const existingIndex = updatedPhases.findIndex(p => p.phase_type === editingSlot.type)

    if (existingIndex >= 0) {
      updatedPhases[existingIndex] = phaseConfig
    } else {
      updatedPhases.push(phaseConfig)
    }

    updateValue(updatedPhases)
    setShowConfigPanel(false)
    setEditingSlot(null)
  }

  // Delete phase
  const handleDeletePhase = () => {
    const updatedPhases = localValue.filter(p => p.phase_type !== editingSlot.type)
    updateValue(updatedPhases)
    setShowConfigPanel(false)
    setEditingSlot(null)
  }

  // Format price display
  const formatPrice = (amount, currency) => {
    if (amount === 0) return 'Free'
    return formatMoney(amount, currency?.toUpperCase(), { cents: true, locale: 'en' })
  }

  // Format duration display
  const formatDuration = (iterations, interval, intervalCount) => {
    if (!iterations) return 'Ongoing'
    const intervalLabel = intervalCount > 1 ? `${intervalCount} ${interval}s` : interval
    const cycleLabel = iterations === 1 ? 'cycle' : 'cycles'
    return `${iterations} ${cycleLabel} (${intervalLabel})`
  }

  // Render slot cards with arrows
  const renderSlots = () => {
    return (
      <div className="flex items-center gap-4">
        {PHASE_SLOTS.map((slot, index) => {
          const phase = getPhaseForSlot(slot.type)
          const isConfigured = !!phase

          return (
            <React.Fragment key={slot.type}>
              <button
                onClick={() => handleSlotClick(slot)}
                className={`
                  relative border-2 border-dashed rounded-lg p-6 transition-all duration-200
                  hover:border-solid hover:shadow-md cursor-pointer flex-1
                  ${isConfigured
                    ? `${slot.color} border-solid`
                    : 'bg-admin_canvas border-admin_bordered hover:bg-admin_paper'
                  }
                `}
              >
                {/* Icon and label */}
                <div className="text-center mb-3">
                  <div className="text-4xl mb-2">{slot.icon}</div>
                  <div className="font-semibold text-admin_text">{slot.label}</div>
                </div>

                {/* Phase details or add button */}
                {isConfigured ? (
                  <div className="text-center">
                    <div className="text-sm font-medium text-admin_text mb-1">
                      {formatPrice(phase.amount, phase.currency)}
                    </div>
                    <div className="text-xs text-admin_text_secondary">
                      {formatDuration(phase.iterations, phase.interval, phase.interval_count)}
                    </div>
                    {!phase.enabled && (
                      <div className="text-xs text-admin_error mt-1">Disabled</div>
                    )}
                    <div className="absolute top-2 right-2">
                      <PencilIcon className="w-4 h-4 text-admin_text_secondary" />
                    </div>
                  </div>
                ) : (
                  <div className="text-center">
                    <PlusIcon className="w-8 h-8 mx-auto text-admin_text_secondary" />
                    <div className="text-xs text-admin_text_secondary mt-1">Click to configure</div>
                  </div>
                )}
              </button>

              {/* Arrow between slots */}
              {index < PHASE_SLOTS.length - 1 && (
                <div className="flex-shrink-0 text-3xl text-admin_text_secondary font-light">
                  →
                </div>
              )}
            </React.Fragment>
          )
        })}
      </div>
    )
  }

  return (
    <div className="phase-slot-editor">
      {/* Slot cards with arrows */}
      {renderSlots()}

      {/* Configuration panel modal */}
      {showConfigPanel && editingSlot && (
        <Modal
          isOpen={showConfigPanel}
          onClose={() => {
            setShowConfigPanel(false)
            setEditingSlot(null)
          }}
          closeOnEscape
          variant="admin"
          maxWidth="500px"
        >
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <span className="text-3xl">{editingSlot.icon}</span>
              <div>
                <h3 className="text-lg font-semibold text-admin_text">
                  Configure {editingSlot.label} Phase
                </h3>
                <p className="text-sm text-admin_text_secondary">
                  Set pricing and duration for this phase
                </p>
              </div>
            </div>

            {/* Form fields */}
            <div className="space-y-4">
              {/* Enabled toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-admin_text">
                  Enable this phase
                </label>
                <input
                  type="checkbox"
                  checked={phaseConfig.enabled ?? true}
                  onChange={(e) => setPhaseConfig({ ...phaseConfig, enabled: e.target.checked })}
                  className="w-5 h-5"
                />
              </div>

              {/* Primary Price checkbox - only for regular phase */}
              {editingSlot.type === 'regular' && (
                <Checkbox
                  isOn={(typeof parentObject === 'function' ? parentObject() : parentObject)?.primary || false}
                  onChange={(checked) => {
                    // Update parent object's primary field
                    if (parentValues) {
                      const parentData = parentValues()
                      parentData.primary = checked
                    }
                  }}
                  label="Primary Price"
                  variant="admin"
                />
              )}

              {/* Price amount */}
              <div>
                <label className="block text-sm font-medium text-admin_text mb-1">
                  Price Amount (cents) *
                </label>
                <Input
                  variant="admin_large"
                  type="number"
                  value={phaseConfig.amount ?? 0}
                  onChange={(e) => setPhaseConfig({ ...phaseConfig, amount: parseInt(e.target.value) || 0 })}
                  placeholder="e.g., 1299 for $12.99"
                />
              </div>

              {/* Currency */}
              <div>
                <label className="block text-sm font-medium text-admin_text mb-1">
                  Currency *
                </label>
                <Input
                  variant="admin_large"
                  value={phaseConfig.currency ?? storeCurrency ?? ''}
                  onChange={(e) => setPhaseConfig({ ...phaseConfig, currency: e.target.value })}
                  placeholder={storeCurrency || ""}
                />
              </div>

              {/* Billing interval */}
              <div>
                <label className="block text-sm font-medium text-admin_text mb-1">
                  Billing Interval *
                </label>
                <DropDownSelect
                  variant="admin"
                  options={[
                    { value: 'day', name: 'Day' },
                    { value: 'week', name: 'Week' },
                    { value: 'month', name: 'Month' },
                    { value: 'year', name: 'Year' }
                  ]}
                  value={[{ value: phaseConfig.interval || 'month', name: phaseConfig.interval || 'month' }]}
                  onChange={(selected) => {
                    if (selected && selected[0]) {
                      setPhaseConfig({ ...phaseConfig, interval: selected[0].value })
                    }
                  }}
                />
              </div>

              {/* Interval count */}
              <div>
                <label className="block text-sm font-medium text-admin_text mb-1">
                  Interval Count
                </label>
                <Input
                  variant="admin_large"
                  type="number"
                  value={phaseConfig.interval_count ?? 1}
                  onChange={(e) => setPhaseConfig({ ...phaseConfig, interval_count: parseInt(e.target.value) || 1 })}
                  min="1"
                />
                <p className="text-xs text-admin_text_secondary mt-1">
                  Billing every X intervals (e.g., 3 for "every 3 months")
                </p>
              </div>

              {/* Iterations */}
              <div>
                <label className="block text-sm font-medium text-admin_text mb-1">
                  Number of Billing Cycles
                </label>
                <Input
                  variant="admin_large"
                  type="number"
                  value={phaseConfig.iterations ?? ''}
                  onChange={(e) => setPhaseConfig({
                    ...phaseConfig,
                    iterations: e.target.value === '' ? null : parseInt(e.target.value)
                  })}
                  placeholder="Leave empty for ongoing"
                />
                <p className="text-xs text-admin_text_secondary mt-1">
                  How many cycles this phase lasts (empty = ongoing)
                </p>
              </div>

              {/* Requires payment method */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-admin_text">
                  Require payment method
                </label>
                <input
                  type="checkbox"
                  checked={phaseConfig.requires_payment_method ?? true}
                  onChange={(e) => setPhaseConfig({
                    ...phaseConfig,
                    requires_payment_method: e.target.checked
                  })}
                  className="w-5 h-5"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-admin_bordered">
              <div>
                {isSlotConfigured(editingSlot.type) && (
                  <Button
                    variant="admin_secondary"
                    onClick={handleDeletePhase}
                    className="text-admin_error border-admin_error hover:bg-admin_error hover:text-white"
                  >
                    Delete Phase
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="admin_secondary"
                  onClick={() => {
                    setShowConfigPanel(false)
                    setEditingSlot(null)
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="admin"
                  onClick={handleSavePhase}
                >
                  Save Phase
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default PhaseSlotEditor
